import React, { useEffect, useRef } from "react";
// HTML/CSS 3D canvas slot — builds a 5-face cube (front + 4 wraparound
// sides) using native CSS perspective + transforms instead of hand-rolled
// projection math. Each face is a positioned <div>; the user's photo is a
// single <img> per face, sized to the full wrap region (imgW × imgH) and
// positioned via translateX/Y so each face shows the correct slice.
//
// Animation: when slot.rotationYTo is set and differs from slot.rotationY,
// the cube oscillates between the two angles via a rAF loop driving the
// cube's CSS rotateY directly. Same easing curve as the SVG predecessor.
// Per-face brightness dimming (sides fade toward 0.30 when edge-on) is
// implemented as a `<div>` overlay with rgba(0,0,0,alpha) tied to the
// live rotateY angle.
export function CanvasSlot({ slot, urls }) {
    var _a;
    const w = slot.width;
    const h = slot.height;
    const d = slot.depth;
    const rotY = slot.rotationY;
    const rotX = slot.rotationX;
    const rotYTo = slot.rotationYTo;
    const animSeconds = slot.rotationYAnimSeconds || 6;
    const url = (_a = urls[0]) !== null && _a !== void 0 ? _a : null;
    const imgW = w + 2 * d;
    const imgH = h + 2 * d;
    const persp = 1400;
    const animate = rotYTo !== null && Math.abs(rotYTo - rotY) > 0.01 && animSeconds > 0.1;
    // Refs we mutate from a rAF loop so React doesn't re-render every frame.
    const cubeRef = useRef(null);
    const dimRightRef = useRef(null);
    const dimLeftRef = useRef(null);
    // Drive rotateY (and the dependent dimming) via rAF when animation is on.
    // The starting angle is rotY; we oscillate to rotYTo and back over
    // animSeconds. Same cosine easing as the SVG predecessor.
    useEffect(() => {
        if (!animate || !cubeRef.current) {
            // Static: just set the rotation once.
            if (cubeRef.current) {
                cubeRef.current.style.transform = `rotateX(${rotX}deg) rotateY(${rotY}deg)`;
            }
            applyDimming(dimRightRef.current, dimLeftRef.current, rotY);
            return;
        }
        let raf = 0;
        const start = performance.now();
        const tick = (now) => {
            const t = ((now - start) / 1000) % animSeconds;
            // Cosine ease in/out over a full cycle. 0 → rotY, halfway → rotYTo,
            // 1 → rotY.
            const phase = (1 - Math.cos((t / animSeconds) * 2 * Math.PI)) / 2;
            const angle = rotY + (rotYTo - rotY) * phase;
            if (cubeRef.current) {
                cubeRef.current.style.transform = `rotateX(${rotX}deg) rotateY(${angle}deg)`;
            }
            applyDimming(dimRightRef.current, dimLeftRef.current, angle);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [animate, rotX, rotY, rotYTo, animSeconds]);
    if (!url) {
        return React.createElement("div", null);
    }
    // The cube is built inside an absolutely-positioned wrapper filling the
    // slot. transform-style: preserve-3d lets the child faces stay in the
    // wrapper's 3D coordinate space; CSS perspective on the immediate parent
    // gives the depth illusion.
    return (React.createElement("div", { style: {
            position: "absolute",
            left: 0,
            top: 0,
            width: `${w}px`,
            height: `${h}px`,
            perspective: `${persp}px`,
            perspectiveOrigin: "center center",
        } },
        React.createElement("div", { style: {
                position: "absolute",
                left: `${w * 0.175 + 6}px`,
                top: `${h + 4}px`,
                width: `${w * 0.65}px`,
                height: `22px`,
                borderRadius: "50%",
                background: "rgba(0,0,0,0.35)",
                filter: "blur(8px)",
            } }),
        React.createElement("div", { ref: cubeRef, style: {
                position: "absolute",
                left: 0,
                top: 0,
                width: `${w}px`,
                height: `${h}px`,
                transformStyle: "preserve-3d",
                transformOrigin: "center center",
                // Initial transform set inline so first paint matches the rAF
                // loop's first frame; useEffect updates it thereafter.
                transform: `rotateX(${rotX}deg) rotateY(${rotY}deg)`,
            } },
            React.createElement(Face, { url: url, imgW: imgW, imgH: imgH, 
                // Front face: full slot, translated forward by d/2 (cube
                // half-depth toward viewer). Shows the centred (w × h) photo
                // region.
                faceW: w, faceH: h, faceTransform: `translateZ(${d / 2}px)`, photoX: -d, photoY: -d }),
            React.createElement(Face, { url: url, imgW: imgW, imgH: imgH, 
                // Right wrap: d × h face at the cube's +X edge. The Face wrapper
                // is positioned with its centre at the cube centre via
                // `left: calc(50% - faceW/2)`, so the transform just needs to
                // rotate the face's normal to +X and translate forward by w/2
                // along the face's local Z (which is world +X after rotation).
                faceW: d, faceH: h, faceTransform: `rotateY(90deg) translateZ(${w / 2}px)`, photoX: -(d + w), photoY: -d, dimRef: dimRightRef }),
            React.createElement(Face, { url: url, imgW: imgW, imgH: imgH, 
                // Left wrap.
                faceW: d, faceH: h, faceTransform: `rotateY(-90deg) translateZ(${w / 2}px)`, photoX: 0, photoY: -d, dimRef: dimLeftRef }),
            React.createElement(Face, { url: url, imgW: imgW, imgH: imgH, 
                // Top wrap: w × d, at the cube's -Y edge. rotateX(90deg) maps
                // local +Z to world -Y (screen-up in CSS coords).
                faceW: w, faceH: d, faceTransform: `rotateX(90deg) translateZ(${h / 2}px)`, photoX: -d, photoY: 0, staticDim: 0.15 }),
            React.createElement(Face, { url: url, imgW: imgW, imgH: imgH, 
                // Bottom wrap.
                faceW: w, faceH: d, faceTransform: `rotateX(-90deg) translateZ(${h / 2}px)`, photoX: -d, photoY: -(d + h), staticDim: 0.45 }))));
}
// Single cube face. The face div is centred at the cube's origin and
// transformed into position. Inside, the photo is positioned absolutely
// at (photoX, photoY) and sized (imgW, imgH) so the face's visible window
// (faceW × faceH at left=0, top=0) shows the right slice of the photo.
// overflow: hidden clips everything outside the face's bounds.
function Face({ url, imgW, imgH, faceW, faceH, faceTransform, photoX, photoY, dimRef, staticDim, }) {
    return (React.createElement("div", { style: {
            position: "absolute",
            // Position the face's box centred at the cube origin: top-left of
            // the face is at (cubeCenter - faceSize/2). The slot itself is
            // positioned at (0,0) of its parent so cubeCenter == (w/2, h/2)
            // in slot coords — but transforms below apply from the face's
            // own centre via transform-origin: center, so the centring math
            // is captured in the face's left/top offsets.
            left: `calc(50% - ${faceW / 2}px)`,
            top: `calc(50% - ${faceH / 2}px)`,
            width: `${faceW}px`,
            height: `${faceH}px`,
            overflow: "hidden",
            transformStyle: "preserve-3d",
            transformOrigin: "center center",
            transform: faceTransform,
        } },
        React.createElement("img", { src: url, alt: "", style: {
                position: "absolute",
                left: `${photoX}px`,
                top: `${photoY}px`,
                width: `${imgW}px`,
                height: `${imgH}px`,
                objectFit: "cover",
            } }),
        React.createElement("div", { ref: dimRef, style: {
                position: "absolute",
                inset: 0,
                background: "black",
                opacity: staticDim !== null && staticDim !== void 0 ? staticDim : 0,
                pointerEvents: "none",
            } })));
}
// Update the right/left dim overlay alphas to match the cube's current
// rotateY angle. Matches the FaceDef.brightness curves from the SVG
// implementation so the visual matches.
function applyDimming(rightDim, leftDim, rotY) {
    const rad = (rotY * Math.PI) / 180;
    // Right brightness = 0.3 + 0.65 * max(0, -sin(rotY)); dim alpha is 1 - brightness.
    const rightBright = 0.3 + 0.65 * Math.max(0, -Math.sin(rad));
    const leftBright = 0.3 + 0.65 * Math.max(0, Math.sin(rad));
    if (rightDim)
        rightDim.style.opacity = `${1 - rightBright}`;
    if (leftDim)
        leftDim.style.opacity = `${1 - leftBright}`;
}
